/**
 * Minimal, typed `fetch` wrapper that all SDK resource modules go through.
 *
 * Design:
 * - Exactly one HTTP call site. Auth header + content-type + error
 *   shaping live here; resource modules are thin method wrappers.
 * - No dependency on a generated OpenAPI types file yet — the typed
 *   `paths` map will plug in via `client.GET<'/some/path'>(...)` once
 *   `scripts/generate-types.ts` is wired up.
 */
import { errorFromResponse, type ApiErrorBody } from './errors.js';

export interface ClientConfig {
  /** API base URL, e.g. `https://api.runcofactor.com` — no trailing slash. */
  baseUrl: string;
  /**
   * Bearer token (JWT) or API key. A function is accepted so callers
   * can refresh on 401 without reconstructing the client.
   */
  token?: string | (() => string | Promise<string>);
  /** Custom fetch implementation (tests, proxies). Defaults to global. */
  fetch?: typeof fetch;
  /** Additional default headers merged into every request. */
  headers?: Record<string, string>;
}

export interface RequestOptions {
  /** Path params substituted into `:name` placeholders in the URL. */
  params?: Record<string, string | number>;
  /** Query params appended to the URL. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** JSON body (stringified automatically). */
  body?: unknown;
  /** Per-request header overrides. */
  headers?: Record<string, string>;
  /** AbortSignal forwarded to fetch. */
  signal?: AbortSignal;
}

export interface Client {
  readonly baseUrl: string;
  request<T = unknown>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    opts?: RequestOptions
  ): Promise<T>;
  /** Replace the stored token (e.g. after org switch). */
  setToken(token: string | (() => string | Promise<string>) | undefined): void;
}

function interpolate(path: string, params: Record<string, string | number> | undefined): string {
  if (!params) return path;
  return path.replace(/:([A-Za-z0-9_]+)/g, (_, k: string) => {
    const v = params[k];
    if (v === undefined) throw new Error(`Missing path param :${k} for ${path}`);
    return encodeURIComponent(String(v));
  });
}

function buildQuery(q: RequestOptions['query']): string {
  if (!q) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export function createClient(cfg: ClientConfig): Client {
  let token = cfg.token;
  const fetchImpl = cfg.fetch ?? fetch;

  async function resolveToken(): Promise<string | undefined> {
    if (typeof token === 'function') return token();
    return token;
  }

  async function request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    opts: RequestOptions = {}
  ): Promise<T> {
    const url =
      cfg.baseUrl.replace(/\/$/, '') +
      interpolate(path, opts.params) +
      buildQuery(opts.query);

    const headers: Record<string, string> = {
      accept: 'application/json',
      ...cfg.headers,
      ...opts.headers,
    };
    const resolvedToken = await resolveToken();
    if (resolvedToken && !headers.authorization) {
      headers.authorization = `Bearer ${resolvedToken}`;
    }
    if (opts.body !== undefined && !headers['content-type']) {
      headers['content-type'] = 'application/json';
    }

    const res = await fetchImpl(url, {
      method,
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });

    const text = await res.text();
    let parsed: unknown;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }

    if (!res.ok) {
      const body = (parsed && typeof parsed === 'object' ? parsed : undefined) as
        | ApiErrorBody
        | undefined;
      throw errorFromResponse(res.status, body);
    }
    return parsed as T;
  }

  return {
    get baseUrl() {
      return cfg.baseUrl;
    },
    request,
    setToken(next) {
      token = next;
    },
  };
}
